import { useKnowledgeDocumentDataContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentDataContext'
import { useResolvedDocumentPageImage } from 'features/knowledgeDocumentViewer/hooks/useResolvedDocumentPageImage'
import { useSingleRetry } from 'features/knowledgeDocumentViewer/hooks/useSingleRetry'
import { isCmmsRecordId } from 'features/knowledgeDocumentViewer/workOrders/workOrder'
import { WorkOrderThumbnail } from 'features/knowledgeDocumentViewer/workOrders/WorkOrderThumbnail'
import { useCallback, useState } from 'react'
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch'
import styled, { keyframes } from 'styled-components'

export const DEFAULT_THUMBNAIL_LANDSCAPE_ASPECT = 72 / 51.48
const DEFAULT_MAX_DIMENSION_REM = 6

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`

function getThumbnailSizeRem(
  aspectRatio: number,
  maxRem: number
): { widthRem: number; heightRem: number } {
  if (aspectRatio >= 1) {
    return { widthRem: maxRem, heightRem: maxRem / aspectRatio }
  }

  return { widthRem: maxRem * aspectRatio, heightRem: maxRem }
}

const ThumbnailShell = styled.div<{
  $widthRem: number
  $heightRem: number
}>`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: ${({ $widthRem }) => $widthRem}rem;
  height: ${({ $heightRem }) => $heightRem}rem;
  overflow: hidden;
  border-radius: 0.0625rem;
`

const Skeleton = styled.div<{ $widthRem: number; $heightRem: number }>`
  width: ${({ $widthRem }) => $widthRem}rem;
  height: ${({ $heightRem }) => $heightRem}rem;
  border-radius: 0.25rem;
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.forecast.cardGroupBackground} 25%,
    ${({ theme }) => theme.forecast.hoverButton} 50%,
    ${({ theme }) => theme.forecast.cardGroupBackground} 75%
  );
  background-size: 800px 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
`

const StyledTransformComponent = styled(TransformComponent)`
  width: 100%;
  height: 100%;

  .react-transform-component {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`

const PlainImage = styled.img<{ $objectFit: 'contain' | 'cover' }>`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: ${({ $objectFit }) => $objectFit};
`

interface DocumentThumbnailProps {
  documentId: string
  alt: string
  filename?: string
  /** Bare BigQuery document_id for UUID5 cantilever page-host derive. */
  bqDocumentId?: string
  /** Cantilever-document host when already known from a hydrated system document. */
  cantileverDocumentEntryId?: string
  /**
   * True when host fields were taken from an already-loaded system document
   * (including when the cantilever host is absent).
   */
  documentHostsHydrated?: boolean
  zoomable?: boolean
  objectFit?: 'contain' | 'cover'
  maxDimensionRem?: number
  pageNumber?: number
}

type SiteDocumentThumbnailProps = DocumentThumbnailProps & { siteId: string }

export function DocumentThumbnail({
  siteId,
  ...props
}: DocumentThumbnailProps & { siteId?: string }) {
  if (isCmmsRecordId(props.documentId)) return <WorkOrderThumbnail {...props} />
  if (siteId === undefined) return <ContextSiteDocumentThumbnail {...props} />
  return <SiteDocumentThumbnail siteId={siteId} {...props} />
}

function ContextSiteDocumentThumbnail(props: DocumentThumbnailProps) {
  const { siteID } = useKnowledgeDocumentDataContext()
  return <SiteDocumentThumbnail siteId={siteID} {...props} />
}

function SiteDocumentThumbnail({
  siteId,
  documentId,
  alt,
  filename,
  bqDocumentId,
  cantileverDocumentEntryId,
  documentHostsHydrated = false,
  zoomable = false,
  objectFit = 'contain',
  maxDimensionRem = DEFAULT_MAX_DIMENSION_REM,
  pageNumber = 1,
}: SiteDocumentThumbnailProps) {
  const { thumbnailUrl, isThumbLoading, refreshThumbnail } =
    useResolvedDocumentPageImage(documentId, siteId, {
      skipHiRes: true,
      pageNumber,
      knownHosts: {
        systemDocumentEntryId: documentId,
        filename,
        bqDocumentId,
        cantileverDocumentEntryId,
        hasHydratedDocumentHosts: documentHostsHydrated,
      },
    })
  const [aspectRatio, setAspectRatio] = useState(
    DEFAULT_THUMBNAIL_LANDSCAPE_ASPECT
  )

  const displayUrl = thumbnailUrl
  const isLoading = !displayUrl && isThumbLoading

  const { widthRem, heightRem } = getThumbnailSizeRem(
    aspectRatio,
    maxDimensionRem
  )

  const updateAspectRatio = useCallback(
    (naturalWidth: number, naturalHeight: number) => {
      if (naturalWidth <= 0 || naturalHeight <= 0) {
        return
      }

      setAspectRatio(naturalWidth / naturalHeight)
    },
    []
  )

  const retryRefresh = useCallback(() => {
    void refreshThumbnail()
  }, [refreshThumbnail])
  const { onFailure: handleImageError, onSuccess: onImageLoaded } =
    useSingleRetry(retryRefresh)

  const handleImageLoad = useCallback(
    (event: React.SyntheticEvent<HTMLImageElement>) => {
      onImageLoaded()
      const { naturalWidth, naturalHeight } = event.currentTarget
      updateAspectRatio(naturalWidth, naturalHeight)
    },
    [onImageLoaded, updateAspectRatio]
  )

  const handleImageRef = useCallback(
    (image: HTMLImageElement | null) => {
      if (!image?.complete || image.naturalWidth <= 0) {
        return
      }

      updateAspectRatio(image.naturalWidth, image.naturalHeight)
    },
    [updateAspectRatio]
  )

  if (isLoading) {
    return <Skeleton $widthRem={widthRem} $heightRem={heightRem} />
  }

  if (!displayUrl) {
    return null
  }

  const image = (
    <PlainImage
      ref={objectFit === 'contain' ? handleImageRef : undefined}
      src={displayUrl}
      alt={alt}
      $objectFit={objectFit}
      onLoad={objectFit === 'contain' ? handleImageLoad : undefined}
      onError={handleImageError}
    />
  )

  if (objectFit === 'cover') {
    return (
      <PlainImage
        ref={handleImageRef}
        src={displayUrl}
        alt={alt}
        $objectFit={objectFit}
        onLoad={handleImageLoad}
        onError={handleImageError}
      />
    )
  }

  if (zoomable) {
    return (
      <ThumbnailShell $widthRem={widthRem} $heightRem={heightRem}>
        <TransformWrapper
          minScale={1}
          maxScale={10}
          limitToBounds
          wheel={{ step: 0.5 }}
          doubleClick={{ disabled: false, mode: 'reset' }}
          pinch={{ disabled: false }}>
          <StyledTransformComponent>{image}</StyledTransformComponent>
        </TransformWrapper>
      </ThumbnailShell>
    )
  }

  return (
    <ThumbnailShell $widthRem={widthRem} $heightRem={heightRem}>
      {image}
    </ThumbnailShell>
  )
}
